'use client';

import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface QuizSessionRecord {
  id: string;
  playerName: string;
  score: number;
  total: number;
  xp: number;
  difficulty: string;
  playedAt: string;
}

export interface LeaderboardEntry {
  id: string;
  playerName: string;
  totalXp: number;
  streak: number;
  sessionsPlayed: number;
  avgAccuracy: number;
  lastPlayedAt: string | null;
}

export interface UpsertLeaderboardParams {
  playerName: string;
  xpGained: number;
  score: number;
  total: number;
  streak: number;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const quizService = {
  /**
   * Save a completed quiz session to Supabase.
   */
  async saveQuizSession(params: {
    playerName?: string;
    score: number;
    total: number;
    xp: number;
    difficulty: string;
  }): Promise<string | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('quiz_sessions')
        .insert({
          player_name: params.playerName ?? 'anonymous',
          score: params.score,
          total: params.total,
          xp: params.xp,
          difficulty: params.difficulty,
          played_at: new Date().toISOString(),
        })
        .select('id')
        .single();

      if (error) {
        console.warn('Quiz session save error:', error.message);
        return null;
      }
      return data?.id ?? null;
    } catch (err) {
      console.warn('Quiz session save failed:', err);
      return null;
    }
  },

  /**
   * Upsert leaderboard entry for a player (insert or update).
   */
  async upsertLeaderboard(params: UpsertLeaderboardParams): Promise<void> {
    const supabase = createClient();
    try {
      // Try to find existing entry
      const { data: existing } = await supabase
        .from('leaderboard_entries')
        .select('id, total_xp, sessions_played, avg_accuracy')
        .eq('player_name', params.playerName)
        .single();

      if (existing) {
        const newSessions = existing.sessions_played + 1;
        const newAccuracy = (
          (existing.avg_accuracy * existing.sessions_played + (params.score / params.total) * 100) /
          newSessions
        );
        await supabase
          .from('leaderboard_entries')
          .update({
            total_xp: existing.total_xp + params.xpGained,
            streak: params.streak,
            sessions_played: newSessions,
            avg_accuracy: Math.round(newAccuracy * 100) / 100,
            last_played_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id);
      } else {
        await supabase.from('leaderboard_entries').insert({
          player_name: params.playerName,
          total_xp: params.xpGained,
          streak: params.streak,
          sessions_played: 1,
          avg_accuracy: Math.round((params.score / params.total) * 100 * 100) / 100,
          last_played_at: new Date().toISOString(),
        });
      }
    } catch (err) {
      console.warn('Leaderboard upsert failed:', err);
    }
  },

  /**
   * Fetch top leaderboard entries.
   */
  async fetchLeaderboard(limit = 20): Promise<LeaderboardEntry[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('leaderboard_entries')
        .select('*')
        .order('total_xp', { ascending: false })
        .limit(limit);

      if (error) {
        console.warn('Leaderboard fetch error:', error.message);
        return [];
      }

      return (data ?? []).map((row) => ({
        id: row.id,
        playerName: row.player_name,
        totalXp: row.total_xp,
        streak: row.streak,
        sessionsPlayed: row.sessions_played,
        avgAccuracy: row.avg_accuracy,
        lastPlayedAt: row.last_played_at,
      }));
    } catch (err) {
      console.warn('Leaderboard fetch failed:', err);
      return [];
    }
  },
};
